/**
 * The UI language. Each app sets it once, from its entry point before any
 * screen code loads, and changing it restarts the app; so a text catalog can
 * be picked when its module loads and read anywhere after. Until an app sets
 * it, as in tests, the language is English and Intl formats in the runtime's
 * default locale.
 *
 * Shared packages pick their catalogs inside functions (`pick`), never at
 * module load, so they stay correct whichever module loads first.
 */
export const LOCALES = ["en", "de"] as const;
export type Locale = (typeof LOCALES)[number];

/** Each language's own name, shown untranslated in language pickers. */
export const LOCALE_NAMES: Readonly<Record<Locale, string>> = {
  en: "English",
  de: "Deutsch",
};

let current: Locale = "en";
let intlLocale: string | undefined;

export function isLocale(value: unknown): value is Locale {
  return typeof value === "string" && (LOCALES as ReadonlyArray<string>).includes(value);
}

function languageOf(tag: string): string {
  return tag.trim().toLowerCase().split(/[-_]/)[0] ?? "";
}

// `de-AT` → `AT`, `zh-Hant-TW` → `TW`; no region → undefined.
function regionOf(tag: string): string | undefined {
  return tag
    .trim()
    .split(/[-_]/)
    .slice(1)
    .find((part) => /^(?:[A-Za-z]{2}|\d{3})$/.test(part))
    ?.toUpperCase();
}

/** The first supported language among the user's preferred ones, else English. */
export function matchLocale(preferred: ReadonlyArray<string>): Locale {
  for (const tag of preferred) {
    const language = languageOf(tag);
    if (isLocale(language)) return language;
  }
  return "en";
}

/**
 * Sets the UI language. `systemTag` is the device's locale (`de-AT`,
 * `en-GB`): dates and numbers are written in the UI language with the
 * device region's conventions, so English on a German Mac still shows a
 * 24-hour clock (`en-DE`).
 */
export function setLocale(locale: Locale, systemTag?: string): void {
  current = locale;
  if (systemTag && languageOf(systemTag) === locale) {
    intlLocale = systemTag;
    return;
  }
  const region = systemTag ? regionOf(systemTag) : undefined;
  intlLocale = region ? `${locale}-${region}` : locale;
}

export function getLocale(): Locale {
  return current;
}

/** The locale tag for `Intl` formatters and `toLocale*String` calls. */
export function getIntlLocale(): string | undefined {
  return intlLocale;
}

/** The active language's entry of a per-language record. */
export function pick<T>(catalogs: Readonly<Record<Locale, T>>): T {
  return catalogs[current];
}
